/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import {
  ReCaptchaResponse,
  PropsFormTypes,
  ReCaptchaModel,
  ReCaptchaFormConfigResult,
} from './types/recaptcha.types';
import {
  recaptchaMessage,
  recaptchaBadgeSelector,
  typeDefaultForm,
} from './configs';
import {
  extendConfig,
  setConfigStorage,
  getConfigStorage,
  checkRecaptchaBadge,
  convertKeysToCamelCase,
} from './lib';
import {
  getRecaptchaToken,
  verifyReCaptchaLoad,
} from './services/recaptcha.service';
import { buildRecaptchaFormConfigQuery } from './graphql/recaptchaConfig.graphql';

import { FetchGraphQL } from '@adobe-commerce/fetch-graphql';

export const recaptchaFetchApi = new FetchGraphQL().getMethods();

const RECAPTCHA_V3_SCRIPT_URL = 'https://www.google.com/recaptcha/api.js';
const RECAPTCHA_ENTERPRISE_SCRIPT_URL = 'https://www.google.com/recaptcha/enterprise.js';

const LOG_PREFIX = '[ReCaptcha]';

export class RecaptchaModule {
  _enableReCAPTCHA: boolean = false;
  _recaptchaBackendEndpoint: string = recaptchaFetchApi.getConfig()?.endpoint || '';
  _recaptchaScriptUrl: string = RECAPTCHA_V3_SCRIPT_URL;
  _configStorageKey: string = 'recaptchaConfig';
  _logger: boolean = false;

  _log(...args: any[]) {
    this._logger && console.log(LOG_PREFIX, ...args);
  }

  _normalizeFormConfigResponse(
    response: any,
    formTypes: string[]
  ): ReCaptchaResponse | undefined {
    const data = response?.data;
    if (!data) return undefined;

    const enabledForms: string[] = [];
    let globalConfig: ReCaptchaFormConfigResult | null = null;
    const detectedTypes = new Set<string>();

    for (const formType of formTypes) {
      const formData = data[formType];

      if (!formData?.is_enabled) continue;

      // configurations may be an array or a single object
      const config = Array.isArray(formData.configurations)
        ? formData.configurations[0]
        : formData.configurations;

      if (!config) continue;

      // Track all reCAPTCHA types across enabled forms, regardless of website_key
      if (config.re_captcha_type) {
        detectedTypes.add(config.re_captcha_type);
      }

      // Skip forms with empty website_key — enabled but not usable
      if (!config.website_key) {
        this._log(
          `Skipping ${formType}: enabled but missing website_key`
        );
        continue;
      }

      enabledForms.push(formType);

      if (!globalConfig) {
        globalConfig = config;
      }
    }

    // Abort if mixed reCAPTCHA types detected across enabled forms
    if (detectedTypes.size > 1) {
      console.warn(
        LOG_PREFIX,
        recaptchaMessage.mixedTypesWarning,
        `\nDetected: ${[...detectedTypes].join(', ')}`
      );
      return undefined;
    }

    if (!globalConfig || enabledForms.length === 0) {
      this._log('No enabled forms found in recaptchaFormConfig response');
      return undefined;
    }

    // Map ReCaptchaConfiguration fields to the existing recaptchaConfig shape
    const isEnterprise =
      globalConfig.re_captcha_type === 'RECAPTCHA_ENTERPRISE';

    this._log(
      `Using reCAPTCHA ${isEnterprise ? 'Enterprise' : 'v3'}`,
      `| Enabled forms: ${enabledForms.join(', ')}`
    );

    return {
      data: {
        recaptchaConfig: {
          is_enabled: true,
          website_key: globalConfig.website_key,
          badge_position: globalConfig.badge_position,
          language_code: globalConfig.language_code,
          minimum_score: globalConfig.minimum_score,
          failure_message: globalConfig.validation_failure_message,
          theme: globalConfig.theme,
          forms: enabledForms,
          recaptcha_type: isEnterprise ? 'enterprise' : 'v3',
        },
      },
    };
  }

  async _updateBadgePosition(
    badgeId: string,
    config: ReCaptchaModel
  ): Promise<void | null> {
    if (!config) return;

    if (config?.badgePosition === 'inline') {
      this._log(`Rendering inline badge for: ${badgeId}`);
      await verifyReCaptchaLoad(
        badgeId,
        config,
        this._logger,
        config.recaptchaType === 'enterprise'
      );
    } else {
      const isBadgeLoaded = await checkRecaptchaBadge();

      if (!isBadgeLoaded) return;

      const recaptchaBadge = document.querySelector(
        recaptchaBadgeSelector
      ) as HTMLIFrameElement;

      const shouldUpdateSrc =
        config.theme &&
        recaptchaBadge &&
        !recaptchaBadge.src.includes('theme=dark') &&
        !recaptchaBadge.src.includes('theme=light');

      if (shouldUpdateSrc) {
        recaptchaBadge.setAttribute(
          'src',
          `${recaptchaBadge.src}&theme=${config.theme}`
        );
      }
    }
  }

  async _addRecaptchaScript(): Promise<void> {
    const config = await this._loadConfig();

    if (!document.getElementById('recaptchaId') && config) {
      const webApiKey = config.websiteKey;
      const isBadgeGlobal = config.badgePosition === 'inline';
      const languageCode = config.languageCode;

      if (!webApiKey) {
        this._log('No website key found, skipping script injection');
        return;
      }

      const scriptUrl =
        config.recaptchaType === 'enterprise'
          ? RECAPTCHA_ENTERPRISE_SCRIPT_URL
          : this._recaptchaScriptUrl;

      const script = document.createElement('script');
      script.setAttribute('id', 'recaptchaId');
      script.defer = true;
      script.src = isBadgeGlobal
        ? `${scriptUrl}?render=${webApiKey}&badge=none&hl=${languageCode}`
        : `${scriptUrl}?render=${webApiKey}&badge=${config.badgePosition}&hl=${languageCode}`;

      this._log(`Loading script: ${script.src}`);
      document.head.appendChild(script);
    }
  }

  async _fetchStoreConfig(): Promise<ReCaptchaResponse | undefined> {
    try {
      const formTypes = Object.keys(typeDefaultForm);
      const query = buildRecaptchaFormConfigQuery(formTypes);

      this._log(`Fetching config for forms: ${formTypes.join(', ')}`);

      const response = await recaptchaFetchApi.fetchGraphQl(query, {
        method: 'GET',
        cache: 'force-cache',
      });

      if (response?.errors?.length) {
        this._logger && console.error(response.errors[0].message);

        return;
      }

      return this._normalizeFormConfigResponse(response, formTypes);
    } catch (error) {
      this._logger && console.error(`${recaptchaMessage.failedFetch}:`, error);
    }
  }

  async _loadConfig(): Promise<ReCaptchaModel | null> {
    const config = await getConfigStorage(this._configStorageKey);

    if (!config) {
      this._logger && console.error(recaptchaMessage.failedGetStorageConfig);

      return null;
    }

    this._enableReCAPTCHA = !!config.isEnabled;

    return config;
  }

  setEndpoint(url: string) {
    if (!url) return;

    this._recaptchaBackendEndpoint = url;
    recaptchaFetchApi.setEndpoint(url);
  }

  async setConfig(configList: PropsFormTypes[]) {
    try {
      const config = await this._fetchStoreConfig();

      if (!config?.data?.recaptchaConfig) {
        this._log('No reCAPTCHA config returned, clearing storage');
        sessionStorage.removeItem(this._configStorageKey);
        return;
      }

      const transformConfig: ReCaptchaModel = convertKeysToCamelCase(
        config?.data?.recaptchaConfig
      );

      const extendedRecaptchaConfig = extendConfig(transformConfig, configList);

      if (extendedRecaptchaConfig) {
        this._log('Config stored:', {
          type: extendedRecaptchaConfig.recaptchaType ?? 'v3',
          enabled: extendedRecaptchaConfig.isEnabled,
          badge: extendedRecaptchaConfig.badgePosition,
          forms: extendedRecaptchaConfig.forms,
        });
        setConfigStorage(
          this._configStorageKey,
          extendedRecaptchaConfig,
          this._logger
        );
      }
    } catch (error) {
      this._logger &&
        console.error(recaptchaMessage.failedSetStorageConfig, error);

      sessionStorage.removeItem(this._configStorageKey);
    }
  }

  async initReCaptcha(lazyLoadTimeout = 3000) {
    // IIFE added to fix SonarQube error "Promise returned in function argument where a void return was expected"
    setTimeout(() => {
      (async () => {
        try {
          const config = await this._loadConfig();

          if (!config?.forms || !config.isEnabled) {
            this._log('reCAPTCHA disabled or no forms configured, skipping init');
            return;
          }

          this._log(
            `Initializing reCAPTCHA (type: ${config.recaptchaType ?? 'v3'}, badge: ${config.badgePosition})`
          );

          await this._addRecaptchaScript();

          if (config.badgePosition === 'inline') {
            await Promise.all(
              (config.forms as PropsFormTypes[]).map((element) =>
                this._updateBadgePosition(element.badgeId, config)
              )
            );
          } else {
            await this._updateBadgePosition('', config);
          }
        } catch (error) {
          this._logger &&
            console.error(recaptchaMessage.failedInitializing, error);
        }
      })();
    }, lazyLoadTimeout);
  }

  async verifyReCaptcha(): Promise<string | undefined> {
    try {
      const config = await this._loadConfig();

      if (!config?.forms || !config.websiteKey || !config.isEnabled) {
        this._log('verifyReCaptcha skipped: reCAPTCHA not enabled or missing config');
        return undefined;
      }

      this._log(
        `Requesting token (type: ${config.recaptchaType ?? 'v3'})`
      );

      const token = await getRecaptchaToken(
        config.websiteKey,
        config.recaptchaType === 'enterprise'
      );

      this._log('Token obtained successfully');

      return token;
    } catch (error) {
      this._logger && console.error(error);
    }
  }

  enableLogger(logger: boolean) {
    this._logger = logger;
  }

  getMethods() {
    return {
      enableLogger: this.enableLogger.bind(this),
      setEndpoint: this.setEndpoint.bind(this),
      setConfig: this.setConfig.bind(this),
      initReCaptcha: this.initReCaptcha.bind(this),
      verifyReCaptcha: this.verifyReCaptcha.bind(this),
    };
  }
}

const recaptcha = new RecaptchaModule();

const { initReCaptcha, verifyReCaptcha, setEndpoint, setConfig, enableLogger } =
  recaptcha.getMethods();

export { setEndpoint, setConfig, initReCaptcha, verifyReCaptcha, enableLogger };
